"use client";

import { createContext, useContext } from "react";
import type {
  KanbanCardData,
  KanbanColumnData,
  KanbanMenuAction,
  KanbanActiveDrag,
  KanbanBoardSize,
  DropIndicatorState,
} from "./types";

// --- Board Context ---

export interface KanbanBoardContextValue {
  columns: KanbanColumnData[];
  activeDrag: KanbanActiveDrag | null;
  dropIndicator: DropIndicatorState;
  size: KanbanBoardSize;
  menuActions: KanbanMenuAction[];
  onCardMove?: (
    cardId: string,
    sourceColumnId: string,
    targetColumnId: string,
    newIndex: number,
  ) => void;
  onColumnMove?: (columnId: string, newIndex: number) => void;
  onAddCard?: (columnId: string) => void;
  onCardClick?: (card: KanbanCardData) => void;
  onCardMenuAction?: (card: KanbanCardData, action: KanbanMenuAction) => void;
}

const KanbanBoardContext = createContext<KanbanBoardContextValue | null>(null);

export const KanbanBoardProvider = KanbanBoardContext.Provider;

export function useKanbanBoard() {
  const ctx = useContext(KanbanBoardContext);
  if (!ctx) throw new Error("useKanbanBoard must be used within a KanbanBoard");
  return ctx;
}

// --- Column Context ---

export interface KanbanColumnContextValue {
  column: KanbanColumnData;
  isOver: boolean;
  isDragging: boolean;
}

const KanbanColumnContext = createContext<KanbanColumnContextValue | null>(
  null,
);

export const KanbanColumnProvider = KanbanColumnContext.Provider;

export function useKanbanColumn() {
  const ctx = useContext(KanbanColumnContext);
  if (!ctx)
    throw new Error("useKanbanColumn must be used within a KanbanColumn");
  return ctx;
}

// --- Card Context ---

export interface KanbanCardContextValue {
  card: KanbanCardData;
  columnId: string;
  isDragging: boolean;
}

const KanbanCardContext = createContext<KanbanCardContextValue | null>(null);

export const KanbanCardProvider = KanbanCardContext.Provider;

export function useKanbanCard() {
  const ctx = useContext(KanbanCardContext);
  if (!ctx) throw new Error("useKanbanCard must be used within a KanbanCard");
  return ctx;
}

// --- Column Drag Handle Context ---

export interface KanbanColumnDragHandleContextValue {
  attributes: Record<string, unknown>;
  listeners: Record<string, unknown> | undefined;
}

const KanbanColumnDragHandleContext =
  createContext<KanbanColumnDragHandleContextValue | null>(null);

export const KanbanColumnDragHandleProvider =
  KanbanColumnDragHandleContext.Provider;

export function useKanbanColumnDragHandle() {
  const ctx = useContext(KanbanColumnDragHandleContext);
  if (!ctx)
    throw new Error(
      "useKanbanColumnDragHandle must be used within a KanbanColumn",
    );
  return ctx;
}
